import React, { useEffect, useState } from "react";
import { AddressType, RsvpType } from "../types/types";
import { db } from "../services/firebaseConfig";
import { collection, getDocs } from "firebase/firestore";
import { addRsvp, getRsvpByAddressKey } from "../services/firebase";

function normalizeString(str: string): string {
  return str.toLowerCase().replace(/[^a-z0-9]/g, "");
  // Removes any character not in a-z or 0-9
}

const RSVP: React.FC = () => {
  // ---------------------------
  // State for fetching addresses (Auto-complete)
  // ---------------------------
  const [addresses, setAddresses] = useState<AddressType[]>([]);
  const [typedName, setTypedName] = useState<string>("");
  const [selectedAddress, setSelectedAddress] = useState<AddressType | null>(
    null
  );

  // ---------------------------
  // RSVP Form + Flow
  // ---------------------------
  const [formData, setFormData] = useState<RsvpType>({
    names: [""],
    individualAttendance: [false], // match length of names
    rehearsalDinner: false,
    welcomeDrinks: false,
    wedding: false,
    afterParty: false,
    foodAllergies: "",
    songRequest: "",
    extraneous: "",
  });

  // Step index in RSVP flow (0..7). We use 99 as a “no-event message” step.
  const [step, setStep] = useState<number>(0);

  // Because only some folks are invited to Rehearsal Dinner
  const REHEARSAL_DINNER_INVITEES = [
    "Amanda Sanders, Brandon Sanders",
    "Emily Aguilar, Matt McNamara",
    "Catherine Powell, Garret Powell",
    "Trenton Abend, Emelyn Vandenberg",
    "Owen James, Lauren Beauchamp",
    "Anthony James, Lucky lady",
    "Nico Mamone, Diana Panish",
    "Chris Fisher, Beautiful Soul",
    "Colby Margosian, Guest",
    "Preston Abend, Courtney Abend",
    "Ian",
    "Connor Flanigan, Lovely lady TBA",
    "Julia Wood, Dwight Wood",
    "Mikaela, Kyle, Brynlee",
    "Julie Sanders, Steve Sanders",
    "Stephen Doub, Kara Ginsberg",
    "Jacob Van Buskirk, Hayley Kyle",
    "Zack Beauchamp, Ashley Cohn",
    "Julia McQueary, David Regina",
    "Kim Beauchamp , Tim Beauchamp",
    "Tom McCullough , Stephanie Sierra",
    "James, Claudia possibly If things go well",
    "Jessie Lindstrom, David Lindstrom",
    "Chris McNichols, Janiece McNichols",
  ];

  // Create a Set of normalized strings
  const normalizedRehearsalInvites = new Set(
    REHEARSAL_DINNER_INVITEES.map((entry) => normalizeString(entry))
  );

  // Final message to show on step 7
  const [finalMessage, setFinalMessage] = useState<string>("");

  // This extra state helps us display the correct “no” disclaimers
  // noEvent = which event the user said "No" to
  // noReturnStep = the original step we came from
  // noNextStep = the step to proceed to if they confirm "no"
  const [noEvent, setNoEvent] = useState<
    "" | "rehearsalDinner" | "welcomeDrinks" | "wedding" | "afterParty"
  >("");
  const [noReturnStep, setNoReturnStep] = useState<number>(0);
  const [noNextStep, setNoNextStep] = useState<number>(0);

  // ---------------------------
  // Fetch Addresses on mount
  // ---------------------------
  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const addressesCollectionRef = collection(db, "addresses");
        const data = await getDocs(addressesCollectionRef);
        console.log("Successfully got addresses: \n", data);
        const fetchedAddresses = data.docs.map(
          (doc) => doc.data() as AddressType
        );
        setAddresses(fetchedAddresses);
      } catch (error) {
        console.error("Error fetching addresses: ", error);
      }
    };

    fetchAddresses();
  }, []);

  // Filter addresses as the user types
  const filteredAddresses = addresses.filter((addr) => {
    const joinedName = addr.names.join(" and ").toLowerCase();
    return joinedName.includes(typedName.toLowerCase());
  });

  // ---------------------------
  // Step Navigation Helpers
  // ---------------------------
  const nextStep = () => setStep((prev) => prev + 1);
  const prevStep = () => setStep((prev) => (prev <= 0 ? 0 : prev - 1));

  // For “No” to an event, we open the “no message” step
  // originalStep = the step we’re leaving (e.g. 3 for Welcome Drinks)
  // nextStepAfterNo = the step we’ll move to once user “confirms” they remain no
  const handleNoChoice = (
    eventName: "rehearsalDinner" | "welcomeDrinks" | "wedding" | "afterParty",
    originalStep: number,
    nextStepAfterNo: number
  ) => {
    setFormData((prev) => ({ ...prev, [eventName]: false }));
    setNoEvent(eventName);
    setNoReturnStep(originalStep);
    setNoNextStep(nextStepAfterNo);
    setStep(99); // “No event” special screen
  };

  // When user sees the “no” message and changes their mind
  const revertNoChoice = () => {
    setFormData((prev) => ({ ...prev, [noEvent]: true }));
    setStep(noReturnStep); // go back to the original step
    setNoEvent("");
  };

  // When user sees the “no” message and confirms “I remain no”
  const confirmNoChoice = () => {
    // Keep the event as false, proceed to next step
    setNoEvent("");
    setStep(noNextStep);
  };

  // ---------------------------
  // Auto-Complete Selection
  // ---------------------------
  const handleSelectAddress = async (address: AddressType) => {
    setSelectedAddress(address);
    const joinedNames = address.names.join(" and ");

    const newFormData: RsvpType = {
      names: address.names,
      // Initialize everyone to "false" for attending
      individualAttendance: address.names.map(() => false),
      rehearsalDinner: false,
      welcomeDrinks: false,
      wedding: false,
      afterParty: false,
      foodAllergies: "",
      songRequest: "",
      extraneous: "",
    };
    // store a hidden "__addressKey" if you want for queries
    (newFormData as any).__addressKey = joinedNames;

    setFormData(newFormData);

    // See if we already have an RSVP for this addressKey
    const existingRsvp = await getRsvpByAddressKey(joinedNames);
    if (existingRsvp) {
      // If RSVP exists, skip to final message
      setFinalMessage("We already have your RSVP on record! Thank you.");
      setStep(7);
    } else {
      setStep(1);
    }
  };

  // ---------------------------
  // Step 1: Which individuals can attend overall?
  // ---------------------------
  const handlePersonAttendanceChange = (index: number, isYes: boolean) => {
    setFormData((prev) => {
      const updatedAttendance = [...prev.individualAttendance];
      updatedAttendance[index] = isYes;
      return { ...prev, individualAttendance: updatedAttendance };
    });
  };

  // --- Submit to Firebase ---------------------------------------------
  const handleSubmit = async () => {
    try {
      console.log("handleSubmit called with: ", formData);
      await addRsvp(formData);
      setFinalMessage("Thanks for your RSVP! We look forward to seeing you.");
    } catch (err) {
      console.error("Error submitting RSVP:", err);
      setFinalMessage("Oops. We ran into an error. Please try again.");
    }
    setStep(7);
  };

const handleOverallAttendanceSubmit = async () => {
  const atLeastOne = formData.individualAttendance.some(Boolean);

  if (!atLeastOne) {
    // mark this RSVP as a full decline
    const declinedData: RsvpType = {
      ...formData,
      rehearsalDinner: false,
      welcomeDrinks: false,
      wedding: false,
      afterParty: false,
    };
    // write it
    await addRsvp(declinedData);

    setFinalMessage(
      "Good to know our happiness means nothing to you! Thanks for letting us know."
    );
    setStep(7); // skip rest of flow
  } else {
    nextStep(); // continue normal flow
  }
};
  // ---------------------------
  // Step 6: Additional info
  // ---------------------------
  const handleAdditionalInfoChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // ---------------------------
  // Render “No” disclaimers
  // ---------------------------
  const renderNoEventStep = () => {
    switch (noEvent) {
      case "welcomeDrinks":
        return (
          <div>
            <p className="flow-step-title">We're sorry you're not fun</p>
            <p>Are you sure?</p>
            <div className="d-flex flex-column align-center">
              <button className="p-1 m-1" onClick={revertNoChoice}>
                I change my mind
              </button>
              <button className="p-1 m-1" onClick={confirmNoChoice}>
                I'm not fun
              </button>
            </div>
          </div>
        );
      case "wedding":
        return (
          <div>
            <p className="flow-step-title">Why even come?</p>
            <p>Are you sure?</p>
            <div className="d-flex flex-column align-center">
              <button className="p-1 m-1" onClick={revertNoChoice}>
                You're right, my mistake
              </button>
              <button className="p-1 m-1" onClick={confirmNoChoice}>
                I will cry too much 😩
              </button>
            </div>
          </div>
        );
      case "afterParty":
        return (
          <div>
            <p className="flow-step-title">Who doesn't love a good afters?</p>
            <p>Are you sure?</p>
            <div className="d-flex flex-column align-center">
              <button className="p-1 m-1" onClick={revertNoChoice}>
                I didn't know there were balloon animals, I'd like to change my
                answer
              </button>
              <button className="p-1 m-1" onClick={confirmNoChoice}>
                I am no longer, and possibly never was, fun
              </button>
            </div>
          </div>
        );
      case "rehearsalDinner":
        return (
          <div>
            <p className="flow-step-title">
              We'll be sure to rehearse without you
            </p>
            <p>Are you sure?</p>
            <div className="d-flex flex-column align-center">
              <button className="p-1 m-1" onClick={revertNoChoice}>
                I changed my mind, I'll come!
              </button>
              <button className="p-1 m-1" onClick={confirmNoChoice}>
                I don't need no rehearsal
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  // ---------------------------
  // Render Steps
  // ---------------------------
  const renderStepContent = () => {
    // Step 7 = final message
    if (step === 7) {
      return (
        <div>
          <h2>{finalMessage}</h2>
        </div>
      );
    }

    // Step 99 = “No event” disclaimers
    if (step === 99) {
      return <>{renderNoEventStep()}</>;
    }

    switch (step) {
      // Step 0: Type + Select name from addresses
      case 0:
        return (
          <>
            <p className="flow-step-title">Enter Your Name(s)</p>
            <input
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="Type your name..."
            />
            {typedName && (
              <ul
                style={{
                  border: "1px solid #ccc",
                  listStyle: "none",
                  padding: 0,
                }}
              >
                {filteredAddresses.map((addr, index) => {
                  const joined = addr.names.join(" and ");
                  return (
                    <li
                      key={index}
                      style={{ cursor: "pointer", padding: "4px" }}
                      onClick={() => {
                        setTypedName(joined);
                        handleSelectAddress(addr);
                      }}
                    >
                      {joined}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        );

      // Step 1: Per-person attendance
      case 1:
        return (
          <>
            <p className="flow-step-title">
              Which of you will be able to attend?
            </p>
            <img
              src="/attendance.png"
              alt="Attendance"
              className="img-fluid mb-3"
            />
            <div>
              {formData.names.map((personName, idx) => (
                <div key={idx} style={{ marginBottom: "1rem" }}>
                  <strong>{personName}</strong>
                  <div>
                    <label style={{ marginRight: "1rem" }}>
                      <input
                        type="radio"
                        name={`person_${idx}`}
                        value="yes"
                        checked={formData.individualAttendance[idx] === true}
                        onChange={() => handlePersonAttendanceChange(idx, true)}
                      />
                      &nbsp;Yes
                    </label>
                    <label>
                      <input
                        type="radio"
                        name={`person_${idx}`}
                        value="no"
                        checked={formData.individualAttendance[idx] === false}
                        onChange={() =>
                          handlePersonAttendanceChange(idx, false)
                        }
                      />
                      &nbsp;No
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <button className="p-1 m-1" onClick={handleOverallAttendanceSubmit}>
              Continue
            </button>
          </>
        );

      // Step 2: Rehearsal Dinner if invited
      case 2: {
        const addressKey = selectedAddress
          ? selectedAddress.names.join(", ")
          : "";

        // Normalize their joined names
        const normalizedAddressKey = normalizeString(addressKey);

        // Check if the set of normalized invites has this normalized string
        const isInvited = normalizedRehearsalInvites.has(normalizedAddressKey);

        if (!isInvited) {
          setStep(3); // skip rehearsal dinner if they're not on the special list
          return null;
        }
        return (
          <>
            <p className="flow-step-title">
              Will you attend the Rehearsal + Dinner?
            </p>
            <h5>Friday September 5, 5:00PM - 8:30PM</h5>
            <div className="mt-3">Elegant Casual</div>
            <div className="mt-3 mb-3">
              Rehearse! Some speeches, probably some pretty decent cheeses!
            </div>
            <img
              src="/rehearsal_dinner.png"
              alt="Dinner Setup"
              className="img-fluid mb-3"
            />
            <button
              className="p-1 m-1"
              onClick={() => {
                setFormData((prev) => ({ ...prev, rehearsalDinner: true }));
                nextStep();
              }}
            >
              Yes
            </button>
            <button
              className="p-1 m-1"
              onClick={() => {
                handleNoChoice("rehearsalDinner", 2, 3);
              }}
            >
              No
            </button>
          </>
        );
      }

      // Step 3: Welcome Drinks
      case 3:
        return (
          <>
            <p className="flow-step-title">
              Will you attend the Welcome Drinks?
            </p>
            <h5>8:30PM - 11:00PM</h5>
            <div className="mt-3">
              <a href="https://www.google.com/maps/dir//la+susana+marbella+address/data=!4m6!4m5!1m1!4e2!1m2!1m1!1s0xd72d92f1ac2685b:0x84ac3ace99c485?sa=X&ved=1t:3061&ictx=111">
                Balcon del golf, Urb. Golf Río Real, 85, 29603 Marbella, Málaga,
                Spain
              </a>
            </div>
            <div className="mt-3">Elegant Casual</div>
            <div className="mt-3 mb-3">
              Highly encouraged - welcome party for all guests following
              rehearsal dinner. Meet and greet with a special surprise guest,
              everyone welcome!
            </div>
            <img
              src="/welcome_drinks.png"
              alt="Venue"
              className="img-fluid mb-3"
            />
            <button
              className="p-1 m-1"
              onClick={() => {
                setFormData((prev) => ({ ...prev, welcomeDrinks: true }));
                nextStep();
              }}
            >
              Yes
            </button>
            <button
              className="p-1 m-1"
              onClick={() => {
                handleNoChoice("welcomeDrinks", 3, 4);
              }}
            >
              No
            </button>
          </>
        );

      // Step 4: Wedding
      case 4:
        return (
          <>
            <p className="flow-step-title">Will you attend the Wedding?</p>
            <h5>5:30PM - 10:30PM</h5>
            <div className="mt-3">
              <a href="https://www.google.com/maps/dir//la+susana+marbella+address/data=!4m6!4m5!1m1!4e2!1m2!1m1!1s0xd72d92f1ac2685b:0x84ac3ace99c485?sa=X&ved=1t:3061&ictx=111">
                Balcon del golf, Urb. Golf Río Real, 85, 29603 Marbella, Málaga,
                Spain
              </a>
            </div>
            <div className="mt-2 mb-3">
              Spanish Chic or Semi Formal Attire (Cowboy boots welcome). Inspo
              here:{" "}
              <a href="https://au.pinterest.com/dania3409/andalusian-wedding/">
                Pinterest
              </a>
            </div>
            <img src="/wedding.png" alt="Venue" className="img-fluid mb-3" />
            <button
              className="p-1 m-1"
              onClick={() => {
                setFormData((prev) => ({ ...prev, wedding: true }));
                nextStep();
              }}
            >
              Yes
            </button>
            <button
              className="p-1 m-1"
              onClick={() => {
                handleNoChoice("wedding", 4, 5);
              }}
            >
              No
            </button>
          </>
        );

      // Step 5: After Party
      case 5:
        return (
          <>
            <p className="flow-step-title">
              Will you attend the Day After Celebration?
            </p>
            <p className="flow-step-title">
              and ceremonial kidnapping of the bride?
            </p>
            <div className="p-3">
              <h4>Sunday September 7th 11:00AM</h4>
              <div className="mt-3">
                <a href="https://www.google.com/maps/dir//la+susana+marbella+address/data=!4m6!4m5!1m1!4e2!1m2!1m1!1s0xd72d92f1ac2685b:0x84ac3ace99c485?sa=X&ved=1t:3061&ictx=111">
                  Balcon del golf, Urb. Golf Río Real, 85, 29603 Marbella,
                  Málaga, Spain
                </a>
              </div>
              <div className="mt-3">
                Brunch, mimosas, and hanging out by the pool!
                <br />
                <br />
                The father of the bride, Tim Beauchamp, has graciously
                volunteered to make the first 15 guests balloon animals!! (no
                marsupials).
              </div>
            </div>
            <img
              src="/bride_kidnapping.png"
              alt="Venue"
              className="img-fluid mb-3 h-25"
            />
            <button
              className="p-1 m-1"
              onClick={() => {
                setFormData((prev) => ({ ...prev, afterParty: true }));
                nextStep();
              }}
            >
              Yes
            </button>
            <button
              className="p-1 m-1"
              onClick={() => {
                handleNoChoice("afterParty", 5, 6);
              }}
            >
              No
            </button>
          </>
        );

      // Step 6: Additional info
      case 6:
        return (
          <>
            <p className="flow-step-title">
              Any food allergies or dietary restrictions?
            </p>
            <input
              type="text"
              name="foodAllergies"
              value={formData.foodAllergies}
              onChange={handleAdditionalInfoChange}
              placeholder="Weaknesses, Allergies, restrictions, etc."
              className="mb-5"
            />

            <p className="flow-step-title">
              Song that will get you on the dance floor?
            </p>
            <input
              type="text"
              name="songRequest"
              value={formData.songRequest}
              onChange={handleAdditionalInfoChange}
              placeholder="I will 💯 break it down for..."
              className="mb-5"
            />

            <p className="flow-step-title">
              Anything else you'd like to say? Wishes for the couple?
              Confessions? Your deepest fear?
            </p>
            <input
              type="text"
              name="extraneous"
              value={formData.extraneous}
              onChange={handleAdditionalInfoChange}
              placeholder="This one time at summer camp..."
              className="mb-5"
            />

            <div className="d-flex flex-column align-center">
              <button className="p-1 m-1" onClick={handleSubmit}>
                Finish RSVP
              </button>
              <button className="p-1 m-1" onClick={prevStep}>
                Back
              </button>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  // ---------------------------
  // Component Return
  // ---------------------------
  return (
    <div className="text-content">
      <h1 className="mb-1">Répondez s'il vous plaît!</h1>
      <div className="simple-card w-100">{renderStepContent()}</div>
    </div>
  );
};

export default RSVP;
